import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import style from "./PublicOpportunityDetails.module.css";
import { baseurl } from "../Url";
import Loader from "../Loder/Loader";
import { MdCheckCircle } from "react-icons/md";
import { IoPaperPlaneOutline } from "react-icons/io5";
import { FiShare2 } from "react-icons/fi";

const normalizeMediaToUrls = (value) => {
  if (!value) return [];
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeMediaToUrls(item))
      .filter((url, index, arr) => arr.indexOf(url) === index);
  }
  if (typeof value === "object") {
    const possibleKeys = ["url", "src", "image", "thumbnail", "path"];
    for (const key of possibleKeys) {
      const nested = normalizeMediaToUrls(value[key]);
      if (nested.length > 0) return nested;
    }
  }
  return [];
};

const getMediaType = (url = "") => {
  const cleanUrl = url.split("?")[0].toLowerCase();
  if (cleanUrl.endsWith(".mp4") || cleanUrl.endsWith(".webm") || cleanUrl.endsWith(".ogg")) return "video";
  if (cleanUrl.endsWith(".pdf")) return "pdf";
  return "image";
};

const isStudyOpportunity = (data) =>
  String(data?.type ?? data?.opportunity_type ?? "")
    .toLowerCase()
    .trim() === "study";

const firstNonEmpty = (data, keys) => {
  if (!data) return "";
  for (const key of keys) {
    const value = data[key];
    if (value != null && String(value).trim() !== "") return String(value).trim();
  }
  return "";
};

const formatStudyFeeRange = (data) => {
  const min = data?.fees_min ?? data?.feesMin;
  const max = data?.fees_max ?? data?.feesMax;
  const nMin = Number(min);
  const nMax = Number(max);
  const hasMin = min != null && min !== "" && Number.isFinite(nMin);
  const hasMax = max != null && max !== "" && Number.isFinite(nMax);
  if (hasMin && hasMax) return `₹${nMin.toLocaleString("en-IN")} - ₹${nMax.toLocaleString("en-IN")}`;
  if (hasMin) return `From ₹${nMin.toLocaleString("en-IN")}`;
  if (hasMax) return `Up to ₹${nMax.toLocaleString("en-IN")}`;
  return "—";
};

const normalizeAdmissionSteps = (raw) => {
  if (!raw || !Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (item && typeof item === "object") {
        return String(item.text ?? item.title ?? item.step ?? item.description ?? "").trim();
      }
      return String(item ?? "").trim();
    })
    .filter(Boolean);
};

const PublicOpportunityDetails = () => {
  const { userId, jobId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [profileGuardPopup, setProfileGuardPopup] = useState({
    open: false,
    message: "",
  });
  const currentUserId = localStorage.getItem("USERID");
  const isLoggedIn = !!currentUserId;

  const getAuthBearerToken = () => {
    const token = localStorage.getItem("access-token");
    if (!token) return "";
    if (typeof token === "string" && token.startsWith("{")) {
      try {
        const parsed = JSON.parse(token);
        return parsed?.token ?? parsed ?? token;
      } catch {
        return token;
      }
    }
    return token;
  };

  const isProfileCompletedForApply = async (memberId) => {
    const orgId = localStorage.getItem("ORGID");
    if (!memberId || !orgId) return false;

    const bearer = getAuthBearerToken();
    const response = await fetch(`${baseurl}/banner/data/retrive`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify({
        user_id: memberId,
        org_id: orgId,
      }),
    });

    const data = await response.json();
    const completion = Number(data?.data?.profile_completion ?? 0);
    return Number.isFinite(completion) && completion >= 100;
  };

  const ensureProfileCompletionBeforeApply = async () => {
    if (!currentUserId) return false;
    try {
      const isComplete = await isProfileCompletedForApply(currentUserId);
      if (isComplete) return true;

      setProfileGuardPopup({
        open: true,
        message: "Please complete your profile before applying for this job.",
      });
      return false;
    } catch {
      setProfileGuardPopup({
        open: true,
        message: "We could not verify your profile completion. Please update your profile before applying.",
      });
      return false;
    }
  };

  const checkUserAndProceed = async (onSuccess) => {
    if (!currentUserId) {
      navigate("/login", {
        state: { from: `/opportunities/details/${userId}/${jobId}` },
      });
      return;
    }
    try {
      const res = await fetch(`${baseurl}/check/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: currentUserId }),
      });
      const data = await res.json();
      if (data.status) {
        const canProceed = await ensureProfileCompletionBeforeApply();
        if (canProceed) {
          onSuccess?.();
        }
      } else {
        navigate("/login", {
          state: { from: `/opportunities/details/${userId}/${jobId}` },
        });
      }
    } catch {
      navigate("/login", {
        state: { from: `/opportunities/details/${userId}/${jobId}` },
      });
    }
  };

  const fetchJobData = async () => {
    if (!jobId) {
      setError("Job not found");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const memberId = isLoggedIn ? currentUserId : null;
      const url = memberId
        ? `${baseurl}/vacancies/${jobId}?member_id=${memberId}`
        : `${baseurl}/vacancies/${jobId}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const result = await response.json();

      if (result.status !== "success" || !result.data) {
        setError(result.message || "Job not found");
        setLoading(false);
        return;
      }

      setJobData(result.data);
    } catch (err) {
      setError("Failed to load job details");
    } finally {
      setLoading(false);
    }
  };

  // If landing via shared referral link (?ref=...), validate and store referral metadata in localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const rawCode = params.get("ref") || params.get("ref_code") || params.get("code");
    if (!rawCode) return;

    const fetchReferral = async () => {
      try {
        const res = await fetch(`${baseurl}/referral/link?code=${encodeURIComponent(rawCode)}`);
        const data = await res.json();

        if (data && data.status && data.data && data.data.referral_code) {
          localStorage.setItem("duno_referral_code", data.data.referral_code);
          if (data.data.referrer_user_id) {
            localStorage.setItem("duno_referral_user_id", data.data.referrer_user_id);
          }
          if (data.data.referral_asset_category) {
            localStorage.setItem("duno_referral_asset_category", data.data.referral_asset_category);
          }
          if (data.data.referral_asset_id) {
            // Treat this as the shared job_id (vacancy id)
            localStorage.setItem("duno_referral_asset_id", data.data.referral_asset_id);
          }
        } else {
          localStorage.removeItem("duno_referral_code");
          localStorage.removeItem("duno_referral_user_id");
          localStorage.removeItem("duno_referral_asset_category");
          localStorage.removeItem("duno_referral_asset_id");
        }
      } catch (e) {
        console.warn("Failed to capture referral for opportunity:", e);
      }
    };

    fetchReferral();
  }, [jobId]);

  // Record referral visit once for logged-in visitors (non-referrer),
  // so Community > Referrals tab has an entry even without signup/purchase.
  useEffect(() => {
    if (typeof window === "undefined" || !currentUserId) return;
    const referralCode = localStorage.getItem("duno_referral_code");
    const referrerUserId = localStorage.getItem("duno_referral_user_id");
    const assetCategory = localStorage.getItem("duno_referral_asset_category") || "JOB";
    const assetId = localStorage.getItem("duno_referral_asset_id") || jobId;

    if (!referralCode || !referrerUserId || !assetId) return;
    if (String(referrerUserId) === String(currentUserId)) return;

    const dedupeKey = `duno_referral_visit_recorded_${referralCode}_${currentUserId}_${assetId}`;
    if (sessionStorage.getItem(dedupeKey) === "1") return;

    fetch(`${baseurl}/referral/record-action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referrer_user_id: referrerUserId,
        referee_id: currentUserId,
        referee_username: localStorage.getItem("username") || localStorage.getItem("email") || "",
        referral_asset_category: assetCategory,
        referral_asset_id: assetId,
        action: "LINK_VISIT",
      }),
    })
      .then(() => {
        sessionStorage.setItem(dedupeKey, "1");
      })
      .catch((err) => {
        console.warn("Failed to record referral visit:", err);
      });
  }, [currentUserId, jobId]);

  useEffect(() => {
    fetchJobData();
  }, [jobId, isLoggedIn]);

  useEffect(() => {
    setSelectedMediaIndex(0);
  }, [jobId, jobData?.id]);

  const getEvaluationSteps = (evaluationSteps) => {
    if (!evaluationSteps) return [];
    const evaluations = [];
    if (evaluationSteps.theoreticalAssessments) evaluations.push("Theoretical Assessments");
    if (evaluationSteps.practicalAssessments) evaluations.push("Practical Assessments");
    if (evaluationSteps.behaviouralAssessments) evaluations.push("Behavioural Assessments");
    if (evaluationSteps.technicalInterview) evaluations.push("Technical Interview");
    if (evaluationSteps.hrInterview) evaluations.push("HR Interview");
    return evaluations;
  };

  const salaryRange =
    jobData?.payment_min || jobData?.payment_max
      ? `₹${(jobData?.payment_min || 0).toLocaleString("en-IN")} - ₹${(
          jobData?.payment_max || 0
        ).toLocaleString("en-IN")}`
      : "—";

  const jobImages = [
    ...normalizeMediaToUrls(jobData?.thumbnail),
    ...normalizeMediaToUrls(jobData?.banner),
    ...normalizeMediaToUrls(jobData?.image),
    ...normalizeMediaToUrls(jobData?.images),
    ...normalizeMediaToUrls(jobData?.gallery),
  ].filter((img, index, arr) => arr.indexOf(img) === index);

  const activeImage =
    jobImages[Math.max(0, Math.min(selectedMediaIndex, jobImages.length - 1))] ||
    jobImages[0] ||
    "";
  const activeMediaType = getMediaType(activeImage);
  const hasMedia = jobImages.length > 0;
  const isStudy = isStudyOpportunity(jobData);
  const displayHeading = isStudy
    ? firstNonEmpty(jobData, ["course_title", "courseTitle", "job_title", "name"]) || "Program"
    : jobData?.job_title;
  const studySubtitle = firstNonEmpty(jobData, ["program_type", "programType"]);
  const courseDescription = firstNonEmpty(jobData, [
    "course_description",
    "courseDescription",
    "job_description",
    "description",
  ]);
  const admissionSteps = normalizeAdmissionSteps(jobData?.admission_process ?? jobData?.admissionProcess);

  if (loading) {
    return (
      <div className={`${style.container} ${style.loadingState}`}>
        <Loader />
      </div>
    );
  }

  if (error || !jobData) {
    return (
      <div className={`${style.container} ${style.errorState}`}>
        <div className={style.errorBox}>
          <p>{error || "Job not found"}</p>
          <button onClick={() => navigate("/")}>Go to Home</button>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    try {
      // Share backend OG route so WhatsApp can read metadata and thumbnail.
      let shareUrl = `${baseurl}/opportunities/share/${userId}/${jobId}`;

      // If current viewer is logged-in, generate (or reuse) a referral link for this JOB asset
      if (currentUserId) {
        try {
          const token = localStorage.getItem("access-token");
          let bearer = token;
          if (token && typeof token === "string" && token.startsWith("{")) {
            try {
              const parsed = JSON.parse(token);
              bearer = parsed?.token ?? parsed ?? token;
            } catch {
              bearer = token;
            }
          }

          const res = await fetch(`${baseurl}/referral/link`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(bearer && { Authorization: `Bearer ${bearer}` }),
            },
            body: JSON.stringify({
              user_id: currentUserId,
              asset_category: "JOB",
              asset_id: jobId,
            }),
          });
          const data = await res.json();
          if (data.status && data.data?.referral_code) {
            shareUrl = `${shareUrl}?ref=${data.data.referral_code}`;

            // Store referral metadata locally for the sharer as well
            localStorage.setItem("duno_referral_code", data.data.referral_code);
            if (data.data.referrer_user_id) {
              localStorage.setItem("duno_referral_user_id", data.data.referrer_user_id);
            } else if (currentUserId) {
              localStorage.setItem("duno_referral_user_id", currentUserId);
            }
            localStorage.setItem("duno_referral_asset_category", data.data.referral_asset_category || "JOB");
            if (data.data.referral_asset_id || jobId) {
              localStorage.setItem("duno_referral_asset_id", data.data.referral_asset_id || jobId);
            }
          }
        } catch (err) {
          console.warn("Referral link fetch failed for job:", err);
        }
      }

      await navigator.clipboard.writeText(shareUrl);
      setSharePopupOpen(true);
      setTimeout(() => {
        setSharePopupOpen(false);
      }, 2200);
    } catch (err) {
      console.warn("Share failed:", err);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.content}>
        <div className={style.jobCard}>
          <header className={style.header}>
            <div className={style.headerTop}>
              <div className={`${style.titleRow} ${hasMedia ? "" : style.titleRowNoMedia}`}>
                {hasMedia ? (
                  <div className={style.mediaGallery}>
                    <div className={style.mediaMainColumn}>
                      <div className={style.mediaMainArea}>
                        <div className={style.mediaThumbRail}>
                          {jobImages.map((img, index) => (
                            (() => {
                              const mediaType = getMediaType(img);
                              return (
                            <button
                              key={`${img}-${index}`}
                              type="button"
                              className={`${style.mediaThumbBtn} ${index === selectedMediaIndex ? style.mediaThumbBtnActive : ""}`}
                              onClick={() => setSelectedMediaIndex(index)}
                              aria-label={`View ${mediaType} ${index + 1}`}
                            >
                              {mediaType === "video" ? (
                                <>
                                  <video
                                    src={img}
                                    className={style.mediaThumbImage}
                                    muted
                                    playsInline
                                    preload="metadata"
                                  />
                                  <span className={style.mediaThumbBadge}>VIDEO</span>
                                </>
                              ) : mediaType === "pdf" ? (
                                <div className={style.pdfThumbPreview}>PDF</div>
                              ) : (
                                <img src={img} alt={`Opportunity preview ${index + 1}`} className={style.mediaThumbImage} />
                              )}
                            </button>
                              );
                            })()
                          ))}
                        </div>
                        <div className={style.mainMediaWrap}>
                          {activeMediaType === "video" ? (
                            <video
                              src={activeImage}
                              controls
                              preload="metadata"
                              className={style.mainMediaImage}
                            />
                          ) : activeMediaType === "pdf" ? (
                            <iframe
                              src={activeImage}
                              title={jobData.job_title ? `${jobData.job_title} PDF preview` : "Opportunity PDF preview"}
                              className={style.mainMediaImage}
                            />
                          ) : (
                            <img
                              src={activeImage}
                              alt={jobData.job_title ? `${jobData.job_title} preview` : "Opportunity preview"}
                              className={style.mainMediaImage}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className={style.titleBlock}>
                  <h1>{displayHeading}</h1>
                  {isStudy
                    ? studySubtitle && <p className={style.subtitle}>{studySubtitle}</p>
                    : jobData.designation && <p className={style.subtitle}>{jobData.designation}</p>}
                  {!hasMedia && (
                    <div className={`${style.mediaActionRow} ${style.mediaActionRowCompact}`}>
                      <button
                        type="button"
                        className={style.shareButton}
                        onClick={handleShare}
                        aria-label="Share opportunity link"
                      >
                        <FiShare2 />
                        <span>Share Opportunity</span>
                      </button>
                      {isLoggedIn ? (
                        <ApplyButtonWithCheck
                          vacancyId={jobData.id}
                          initiallyApplied={jobData.has_applied}
                          checkUserAndProceed={checkUserAndProceed}
                          onSuccess={fetchJobData}
                        />
                      ) : (
                        <button
                          className={style.applyBtnAnonymous}
                          onClick={() =>
                            navigate("/login", {
                              state: { from: `/opportunities/details/${userId}/${jobId}` },
                            })
                          }
                        >
                          Apply Now
                        </button>
                      )}
                    </div>
                  )}
                  {!isStudy && (
                    <p className={style.shortDescription}>
                      {jobData.job_description || "No description provided."}
                    </p>
                  )}
                  {isStudy && (
                    <>
                      <div className={style.studyMetaGroup}>
                        <p className={style.studyMetaLine}>
                          <span className={style.studyMetaLabel}>College:</span>
                          <span className={style.studyMetaValue}>
                            {firstNonEmpty(jobData, ["college_name", "collegeName"]) || "—"}
                          </span>
                        </p>
                        <p className={style.studyMetaLine}>
                          <span className={style.studyMetaLabel}>Department:</span>
                          <span className={style.studyMetaValue}>
                            {firstNonEmpty(jobData, ["department"]) || "—"}
                          </span>
                        </p>
                        <p className={style.studyMetaLine}>
                          <span className={style.studyMetaLabel}>Duration:</span>
                          <span className={style.studyMetaValue}>
                            {firstNonEmpty(jobData, ["duration"]) || "—"}
                          </span>
                        </p>
                      </div>

                      <div className={style.studyUniversityStrip}>
                        <p className={style.studyUniversityLabel}>University</p>
                        <p className={style.studyUniversityValue}>
                          {firstNonEmpty(jobData, ["university"]) || "—"}
                        </p>
                      </div>

                      <div className={style.studyQuickCards}>
                        <div className={style.studyQuickCard}>
                          <p className={style.studyQuickLabel}>Study mode</p>
                          <p className={style.studyQuickValue}>
                            {firstNonEmpty(jobData, ["study_mode", "studyMode"]) || "—"}
                          </p>
                        </div>
                        <div className={style.studyQuickCard}>
                          <p className={style.studyQuickLabel}>Fee range (annual)</p>
                          <p className={style.studyQuickValue}>{formatStudyFeeRange(jobData)}</p>
                        </div>
                        <div className={style.studyQuickCard}>
                          <p className={style.studyQuickLabel}>No of seats</p>
                          <p className={style.studyQuickValue}>
                            {jobData.number_of_positions ?? jobData.numberOfPositions ?? 0}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {hasMedia && (
                <div className={style.mediaActionRow}>
                  <button
                    type="button"
                    className={style.shareButton}
                    onClick={handleShare}
                    aria-label="Share opportunity link"
                  >
                    <FiShare2 />
                    <span>Share Opportunity</span>
                  </button>
                  {isLoggedIn ? (
                    <ApplyButtonWithCheck
                      vacancyId={jobData.id}
                      initiallyApplied={jobData.has_applied}
                      checkUserAndProceed={checkUserAndProceed}
                      onSuccess={fetchJobData}
                    />
                  ) : (
                    <button
                      className={style.applyBtnAnonymous}
                      onClick={() =>
                        navigate("/login", {
                          state: { from: `/opportunities/details/${userId}/${jobId}` },
                        })
                      }
                    >
                      Apply Now
                    </button>
                  )}
                </div>
              )}
            </div>
          </header>

          <div className={style.pageLayout}>
            <div className={style.mainColumn}>
              {!isStudy && <div className={style.overviewCardsRow}>
                  <>
                    <div className={style.overviewCard}>
                      <p className={style.overviewCardLabel}>Designation</p>
                      <p className={style.overviewCardValue}>{jobData.designation || "—"}</p>
                    </div>
                    <div className={style.overviewCard}>
                      <p className={style.overviewCardLabel}>Positions</p>
                      <p className={style.overviewCardValue}>{jobData.number_of_positions || 0}</p>
                    </div>
                    <div className={style.overviewCard}>
                      <p className={style.overviewCardLabel}>Employment Category</p>
                      <p className={style.overviewCardValue}>{jobData.category_name || jobData.opportunity_type || "—"}</p>
                    </div>
                    <div className={style.overviewCard}>
                      <p className={style.overviewCardLabel}>Work Mode</p>
                      <p className={style.overviewCardValue}>{jobData.work_mode || "—"}</p>
                    </div>
                    <div className={style.overviewCard}>
                      <p className={style.overviewCardLabel}>Location</p>
                      <p className={style.overviewCardValue}>{jobData.work_location || "—"}</p>
                    </div>
                    <div className={style.overviewCard}>
                      <p className={style.overviewCardLabel}>Salary Range</p>
                      <p className={style.overviewCardValue}>{salaryRange}</p>
                    </div>
                  </>
              </div>}

              <section className={`${style.contentSection} ${style.aboutProgramSection} ${isStudy ? style.studySection : ""}`}>
                <h2 className={`${style.sectionTitle} ${isStudy ? style.studySectionTitle : ""}`}>
                  {isStudy ? "About the program" : "About the Role"}
                </h2>
                <div className={style.contentCard}>
                  <p className={style.detailText}>
                    {isStudy
                      ? courseDescription || "No description provided."
                      : jobData.job_description || "No description provided."}
                  </p>
                </div>
              </section>

              {isStudy ? (
                <div className={style.studyFlowSections}>
                  <section className={`${style.contentSection} ${style.studySection}`}>
                    <h2 className={`${style.sectionTitle} ${style.studySectionTitle}`}>Admission process</h2>
                    <div className={`${style.contentCard} ${style.studyFeatureCard}`}>
                      <div className={style.bulletListWrap}>
                        {admissionSteps.length > 0 ? (
                          <ul className={`${style.bulletList} ${style.studyBulletList}`}>
                            {admissionSteps.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className={style.emptyText}>None specified</span>
                        )}
                      </div>
                    </div>
                  </section>
                  <section className={`${style.contentSection} ${style.studySection}`}>
                    <h2 className={`${style.sectionTitle} ${style.studySectionTitle}`}>Scholarship</h2>
                    <div className={`${style.contentCard} ${style.studyFeatureCard}`}>
                      <p className={`${style.detailText} ${style.studyFeatureText}`}>
                        {firstNonEmpty(jobData, ["scholarship"]) || "None specified"}
                      </p>
                    </div>
                  </section>
                  <section className={`${style.contentSection} ${style.studySection}`}>
                    <h2 className={`${style.sectionTitle} ${style.studySectionTitle}`}>Financial aid</h2>
                    <div className={`${style.contentCard} ${style.studyFeatureCard}`}>
                      <p className={`${style.detailText} ${style.studyFeatureText}`}>
                        {firstNonEmpty(jobData, ["financial_aid", "financialAid"]) || "None specified"}
                      </p>
                    </div>
                  </section>
                </div>
              ) : (
                <div className={style.detailColumns}>
                  <div className={style.detailColumn}>
                    <section className={style.contentSection}>
                      <h2 className={style.sectionTitle}>Pre-requisites</h2>
                      <div className={style.contentCard}>
                        <div className={style.bulletListWrap}>
                          {jobData.prerequisites?.length > 0 ? (
                            <ul className={style.bulletList}>
                              {jobData.prerequisites.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className={style.emptyText}>None specified</span>
                          )}
                        </div>
                      </div>
                    </section>
                    <section className={style.contentSection}>
                      <h2 className={style.sectionTitle}>Mandatory</h2>
                      <div className={style.contentCard}>
                        <div className={style.bulletListWrap}>
                          {jobData.mandatory?.length > 0 ? (
                            <ul className={style.bulletList}>
                              {jobData.mandatory.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className={style.emptyText}>None specified</span>
                          )}
                        </div>
                      </div>
                    </section>
                    <section className={style.contentSection}>
                      <h2 className={style.sectionTitle}>Good to Have</h2>
                      <div className={style.contentCard}>
                        <div className={style.bulletListWrap}>
                          {jobData.good_to_have?.length > 0 ? (
                            <ul className={style.bulletList}>
                              {jobData.good_to_have.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className={style.emptyText}>None specified</span>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                  <div className={style.detailColumn}>
                    <section className={style.contentSection}>
                      <h2 className={style.sectionTitle}>Benefits</h2>
                      <div className={style.contentCard}>
                        <div className={style.bulletListWrap}>
                          {jobData.other_benefits?.length > 0 ? (
                            <ul className={style.bulletList}>
                              {jobData.other_benefits.map((benefit, i) => (
                                <li key={i}>{benefit}</li>
                              ))}
                            </ul>
                          ) : (
                            <span className={style.emptyText}>None specified</span>
                          )}
                        </div>
                      </div>
                    </section>
                    <section className={style.contentSection}>
                      <h2 className={style.sectionTitle}>Evaluation Process</h2>
                      <div className={style.contentCard}>
                        <div className={style.evaluationsList}>
                          {getEvaluationSteps(jobData.evaluation_steps).length > 0 ? (
                            getEvaluationSteps(jobData.evaluation_steps).map((item, i) => (
                              <div key={i} className={style.evaluationItem}>
                                <span className={style.checkboxChecked}>✓</span>
                                <span>{item}</span>
                              </div>
                            ))
                          ) : (
                            <span className={style.emptyText}>None specified</span>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
      {sharePopupOpen && (
        <div className={style.sharePopup}>
          <MdCheckCircle className={style.sharePopupIcon} />
          <p className={style.sharePopupMessage}>Copied link</p>
        </div>
      )}
      {profileGuardPopup.open && (
        <div className={style.profileGuardOverlay}>
          <div className={style.profileGuardModal}>
            <h4>Complete your profile</h4>
            <p>{profileGuardPopup.message}</p>
            <div className={style.profileGuardActions}>
              <button
                type="button"
                className={style.profileGuardSecondaryBtn}
                onClick={() => setProfileGuardPopup({ open: false, message: "" })}
              >
                Not now
              </button>
              <button
                type="button"
                className={style.profileGuardPrimaryBtn}
                onClick={() => {
                  setProfileGuardPopup({ open: false, message: "" });
                  navigate("/profile");
                }}
              >
                Update Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ApplyButtonWithCheck = ({
  vacancyId,
  initiallyApplied,
  checkUserAndProceed,
  onSuccess,
}) => {
  const [wrappedApply, setWrappedApply] = useState(null);

  const handleApply = async () => {
    await checkUserAndProceed(async () => {
      const memberId = localStorage.getItem("USERID");
      if (!memberId || initiallyApplied) return;

      try {
        const response = await fetch(`${baseurl}/vacancies/apply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vacancy_id: vacancyId,
            member_id: memberId,
          }),
        });

        const data = await response.json();
        if (data.status === "success") {
          setWrappedApply(true);
          onSuccess?.();
        } else {
          alert(data.message || "Failed to apply");
        }
      } catch (err) {
        console.error(err);
        alert("Something went wrong");
      }
    });
  };

  const isApplied = initiallyApplied || wrappedApply;

  return (
    <button
      className={`${style.applyBtn} ${isApplied ? style.appliedBtn : ""}`}
      onClick={handleApply}
      disabled={isApplied}
    >
      {isApplied ? (
        "Applied"
      ) : (
        <>
          <IoPaperPlaneOutline />
          <span>Apply Now</span>
        </>
      )}
    </button>
  );
};

export default PublicOpportunityDetails;
